import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { LogoMarkStatic } from "@/components/LogoMark";

// BLOCK 167 ,  seller submit-then-claim. Public page (no auth), reached via
// the emailed claim link (24h expiry). Lets the seller set a password on
// the account that was created server-side the first time their email
// submitted a listing.
export default function SellerClaim() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/seller/claim", { token, password });
      setDone(true);
      toast.success("Account claimed");
      setTimeout(() => navigate("/login", { replace: true }), 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Claim failed");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#05070A] text-slate-100 flex items-center justify-center px-6">
        <div className="ae-card p-8 max-w-md text-center" data-testid="seller-claim-no-token">
          <h2 className="font-heading text-2xl text-rose-400">Invalid claim link</h2>
          <p className="text-sm text-slate-400 mt-2">This link is missing a token.</p>
          <Link to="/" className="btn-primary text-sm mt-4 inline-block">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05070A] text-slate-100 flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />

      <div className="relative z-10 w-full max-w-md">
        <div className="ae-card p-8">
          <div className="flex items-center justify-center mb-6">
            <LogoMarkStatic className="h-20 md:h-[144px]" />
          </div>

          {done ? (
            <div className="text-center py-4" data-testid="seller-claim-done">
              <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
              <h2 className="font-heading text-2xl tracking-tight">Account claimed.</h2>
              <p className="text-sm text-slate-400 mt-2">Redirecting to sign in…</p>
            </div>
          ) : (
            <>
              <div className="text-xs uppercase tracking-widest text-emerald-400 mb-2">Claim your seller account</div>
              <h1 className="font-heading text-3xl tracking-tight">Set your password.</h1>
              <p className="text-sm text-slate-400 mt-2">
                This unlocks a dashboard where you can track every listing tied to this email.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">Password</label>
                  <input
                    data-testid="seller-claim-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-500/60"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">Confirm password</label>
                  <input
                    data-testid="seller-claim-confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={6}
                    className="w-full bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-500/60"
                  />
                </div>
                <button type="submit" disabled={busy} data-testid="seller-claim-submit" className="btn-primary w-full">
                  {busy ? "Claiming…" : "Claim my account"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
